import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import useDebounce from 'hooks/useDebounce';
import useUsersList from 'hooks/API/GET/useUsersList';
import { AppButton, AppTextField } from 'components';
import { AiFillEye } from 'react-icons/ai';
import UserRoleDropDown from 'components/molecules/UserRoleDropDown';
import ESDropDown from 'components/molecules/ESDropDown';
import ESClassDropDown from 'components/molecules/ESClassDropDown';
import PlansSelector from 'components/molecules/PlansSelector';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import TablePaginate from '../../components/Paginate';
import { checkForErrors } from 'utilities/fns';
import useManageUser from 'hooks/API/useManageUser';
import useToaster from 'hooks/useToaster';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import * as AppPics from 'assets/images';

function ManageUser() {
  const [params, setParams] = usePaginateDefaults();
  const [search, handleSearch] = useState('');
  const [role, handleRole] = useState('');
  const [ES, handleES] = useState('');
  const [ESClass, handleESClass] = useState('');
  const [plan, handlePlan] = useState('');
  const [deleteModal, handleDeleteModal] = useState({ show: false, data: {} });

  const { isLoading: deleteLoading, mutate, isError: deleteError } = useManageUser();

  useToaster(deleteLoading, { type: deleteError ? 'error' : 'success' });

  const searchKeyword = useDebounce(search, 1000);

  const _data = useUsersList({
    enabled: true,
    configs: { params: { ...params, role, plan, keyword: searchKeyword, es: ES, class: ESClass } },
  });
  const { isLoading, data: tableData, isError } = _data;
  const { meta: { page, total } = {} } = tableData?.data ?? {};

  const [formErrors, setFormErrors] = useState([]);
  const errors = checkForErrors(formErrors);

  function clearFilter() {
    handleRole('');
    handleES('');
    handleESClass('');
    handlePlan('');
    setFormErrors([]);
  }

  function closeModal() {
    handleDeleteModal({ show: false, data: {} });
  }

  function deleteUser() {
    if (deleteLoading) return;
    mutate({ type: 'DELETE', ...deleteModal.data });
    closeModal();
  }

  return (
    <Fragment>
      <BasicDelete
        open={deleteModal.show}
        closeModal={closeModal}
        Header="Delete user"
        handleConfirmation={deleteUser}
      />
      <div className="mainWraper">
        <div className="container-fluid outerborder">
          <div className="row">
            <div className="col-6">
              <h2 className="prime-head p-0">Manage User</h2>
            </div>
            <div className="col-6 prime-head text-right">
              <AppButton variantClass="btn-plane p--tint mx-2" onClick={clearFilter}>
                Clear filter
              </AppButton>
              <Link to="/manage/users/add">
                <AppButton variantClass="btn-plane g--tint">+ Add User</AppButton>
              </Link>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-lg-4 col-md-6 col-sm-12">
              <UserRoleDropDown
                enableAPI
                value={role}
                handleChange={(event) => handleRole(event.target.value)}
                fieldErrors={errors['role']}
              />
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12">
              <ESDropDown
                enableAPI
                value={ES}
                handleChange={(event) => handleES(event.target.value)}
                defaultLook={false}
              />
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12">
              <ESClassDropDown
                enableAPI={!!ES}
                dependencies={{
                  query: `esId=${ES}`,
                }}
                value={ESClass}
                fieldErrors={errors['class']}
                handleChange={(event) => handleESClass(event.target.value)}
                defaultLook={false}
              />
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-lg-4 col-md-6 col-sm-12 mt-5">
              <PlansSelector
                enableAPI
                selectedValues={plan}
                handleChange={(plan) => {
                  handlePlan(plan._id);
                }}
              />
            </div>
          </div>

          <div className="d-flex flex-row-reverse mt-2">
            <div>
              <AppTextField
                inputProps={{
                  value: search,
                  placeholder: 'Type to search...',
                  onChange: (event) => handleSearch(event.target.value),
                }}
                labelProps={{
                  label: '',
                }}
              />
            </div>
          </div>
          <div className="tableWrap">
            <div className="table-responsive">
              <table className="table tableWrap__table">
                <thead className="thead-light">
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col"></th>
                    <th scope="col">Name</th>
                    <th scope="col">Email/Phone</th>
                    <th scope="col">Role</th>

                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                {isLoading ? (
                  'Loading...'
                ) : isError ? (
                  'Failed to load data'
                ) : tableData?.data?.result?.length > 0 ? (
                  <tbody>
                    {tableData?.data?.result?.map((result, index) => {
                      return (
                        <Fragment>
                          <tr key={result._id}>
                            <th scope="row">{index + 1}</th>
                            <td>{result.key}</td>
                            <td>{result.name}</td>
                            <td>{result?.email ?? result?.mobile}</td>
                            <td>{result?.role?.name}</td>
                            <td>{result?.status}</td>
                            <td>
                              <Link
                                to={`/manage/users/edit/${result?._id}`}
                                className="actions-icons mx-2"
                              >
                                <img src={AppPics.EditSvg} alt="edit this option" />
                              </Link>
                              <AppButton
                                onClick={() =>
                                  handleDeleteModal({
                                    show: true,
                                    data: { ...result },
                                  })
                                }
                                className="actions-icons mx-2 text-danger"
                              >
                                <img src={AppPics.DeleteSvg} alt="delete this option" />
                              </AppButton>
                              <Link
                                to={`/mNge/users/view/${result._id}`}
                                className="actions-icons mx-2"
                              >
                                <AiFillEye />
                              </Link>
                            </td>
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                ) : (
                  'No records found'
                )}
              </table>
            </div>
          </div>
          <TablePaginate
            maxCount={total}
            currentPage={page}
            defaultLimit={params.limit}
            setPage={(page) => setParams((x) => ({ ...x, page }))}
            handleChange={(limit) => setParams((x) => ({ ...x, limit }))}
          />
        </div>
      </div>
    </Fragment>
  );
}

export default ManageUser;
